import type { TurnTrace } from '@/lib/ai/inspection';

/**
 * "Scheduling match" — what Iris resolved a conversation to, read off the
 * inspection trace: the job type it matched, whether it asked (and what the
 * customer said) about home vs business, the address it confirmed, and the
 * business unit operations stamped on the booked visit together with the
 * resolver step that picked it. Derived, not stored: every fact below is a
 * tool argument or result already in the trace.
 */
export type BusinessUnitResolutionMethod = 'SINGLE' | 'SEGMENT' | 'AREA' | 'TECHNICIAN' | 'PRIMARY';

export interface SchedulingMatch {
  jobTypeName: string | null;
  customerSegment: 'RESIDENTIAL' | 'COMMERCIAL' | null;
  /** Iris asked "home or business?" in one of its replies. */
  segmentAsked: boolean;
  address: string | null;
  technicianName: string | null;
  businessUnit: { name: string; code: string; segment: string } | null;
  businessUnitResolutionMethod: BusinessUnitResolutionMethod | null;
  booked: boolean;
}

export const RESOLUTION_METHOD_LABELS: Record<BusinessUnitResolutionMethod, string> = {
  SINGLE: "the job type's only unit",
  SEGMENT: 'the customer type (home / business)',
  AREA: 'the service address area',
  TECHNICIAN: "the technician's unit",
  PRIMARY: "the job type's first unit — nothing narrowed it further",
};

// Order-independent, same rule as the acceptance runner: a home-ish word and
// a business-ish word in a sentence that asks a question.
const HOME_WORDS = /\b(home|house|residen\w*)\b/i;
const BUSINESS_WORDS = /\b(business|commercial|company|office)\b/i;

function asksHomeOrBusiness(reply: string): boolean {
  return reply
    .split(/(?<=[.?!])\s+/)
    .some(
      (sentence) =>
        sentence.includes('?') && HOME_WORDS.test(sentence) && BUSINESS_WORDS.test(sentence),
    );
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  if (typeof value === 'string') {
    try {
      const parsed: unknown = JSON.parse(value);
      return asRecord(parsed);
    } catch {
      return null;
    }
  }
  return null;
}

export function deriveSchedulingMatch(turns: readonly TurnTrace[]): SchedulingMatch | null {
  const match: SchedulingMatch = {
    jobTypeName: null,
    customerSegment: null,
    segmentAsked: false,
    address: null,
    technicianName: null,
    businessUnit: null,
    businessUnitResolutionMethod: null,
    booked: false,
  };
  let touched = false;
  for (const turn of turns) {
    if (turn.replyText && asksHomeOrBusiness(turn.replyText)) match.segmentAsked = true;
    for (const tool of turn.tools) {
      const args = asRecord(tool.args) ?? {};
      const result = asRecord(tool.result) ?? {};
      if (tool.name === 'update_lead') {
        const segment = String(args.customerSegment ?? '').toUpperCase();
        if (segment === 'RESIDENTIAL' || segment === 'COMMERCIAL') {
          match.customerSegment = segment;
          touched = true;
        }
        if (typeof args.address === 'string' && args.address.trim()) {
          match.address = args.address.trim();
          touched = true;
        }
      }
      if (tool.name === 'confirm_service_address' && typeof result.address === 'string') {
        match.address = result.address;
        touched = true;
      }
      if (tool.name === 'check_availability') {
        const jobType = asRecord(asRecord(result.matchedService)?.jobType);
        if (jobType && typeof jobType.name === 'string') {
          match.jobTypeName = jobType.name;
          touched = true;
        }
      }
      if (tool.name === 'reserve_and_book' && result.success === true) {
        const sm = asRecord(result.schedulingMatch);
        const jobType = asRecord(sm?.jobType);
        const unit = asRecord(sm?.businessUnit);
        match.booked = true;
        touched = true;
        // The segment operations actually booked with — from the lead when it
        // was asked in an earlier conversation, or from the contact — beats
        // whatever this trace's update_lead calls said.
        const bookedSegment = String(sm?.customerSegment ?? '').toUpperCase();
        if (bookedSegment === 'RESIDENTIAL' || bookedSegment === 'COMMERCIAL') {
          match.customerSegment = bookedSegment;
        }
        if (jobType && typeof jobType.name === 'string') match.jobTypeName = jobType.name;
        if (typeof sm?.technicianName === 'string') match.technicianName = sm.technicianName;
        if (unit && typeof unit.name === 'string') {
          match.businessUnit = {
            name: unit.name,
            code: String(unit.code ?? ''),
            segment: String(unit.segment ?? 'ANY'),
          };
        }
        const method = String(sm?.businessUnitResolutionMethod ?? '');
        if (method in RESOLUTION_METHOD_LABELS) {
          match.businessUnitResolutionMethod = method as BusinessUnitResolutionMethod;
        }
      }
    }
  }
  return touched ? match : null;
}
